import React, { useMemo } from 'react'

import { useAppSelector } from '../../../../store/hooks'
import { useGetConnectorEntitiesQuery, type WealthboxTag } from '../../../../store/integrations/integrations'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../common/integrations'

import { TagsModal, type TagOption } from '../TagsModal'
import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'

export const WealthboxTagsActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const accountId = useAppSelector(selectAccountId)

  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useCrmEligibility(IntegrationServiceId.WEALTHBOX, results ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.WEALTHBOX,
    sourceObject: 'tags',
    entityType: 'set_tags',
    relatedToType: 'contact',
    successToast: { title: 'Assigning tags', message: 'Wealthbox tag assignment started.' },
    errorToast: { title: 'Failed to assign tags', message: 'Could not start the Wealthbox tag assignment. Please try again.' },
  })

  const { data, isFetching } = useGetConnectorEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.WEALTHBOX,
    entityType: 'tags',
    relatedTo: null,
  })

  const tags: TagOption[] = useMemo(() => {
    const payload = data?.data as Array<{ tags?: WealthboxTag[] }> | undefined
    const rawTags = payload?.flatMap((entry) => entry.tags ?? []) ?? []
    return rawTags.map((tag) => ({ id: String(tag.id), name: tag.name }))
  }, [data])

  const totalCount = results?.length ?? 0
  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  const bodyOverride =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='Wealthbox' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='Wealthbox' />
    ) : null

  const primaryActionDisabled = resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0

  const handleSave = async (selectedTags: TagOption[]) => {
    await save({ tags: selectedTags.map((t) => ({ id: t.id, name: t.name })) })
  }

  return (
    <TagsModal
      show={open}
      onCancel={onClose}
      tags={tags}
      loading={isFetching}
      saving={saving}
      count={totalCount}
      onSave={handleSave}
      primaryActionTitle={noEligibleClients ? 'Done' : 'Assign tags'}
      notice={<CrmEligibilityNotice eligibility={eligibility} verbPhrase='tag' crmName='Wealthbox' />}
      primaryActionDisabled={noEligibleClients ? false : primaryActionDisabled}
      bodyOverride={bodyOverride}
      hideSecondaryAction={noEligibleClients}
      primaryActionOverride={noEligibleClients ? onClose : undefined}
    />
  )
}
